import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

import Logo, { LogoMark } from '../Common/Logo';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '../../theme';
import { getNavigation } from '../../constants/navigation';

// Sidebar is flush with the viewport edges (no floating gutter), so the
// content area touches it directly.
const GUTTER = 0;

const TEXT = '#B7BCDD';
const TEXT_MUTED = '#8E94BE';
const LINE = 'rgba(255,255,255,0.08)';
const WHITE = '#FFFFFF';

const Sidebar = ({ selectedBrand, user, onClose, collapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { main: mainMenuItems, brandGroups, admin: adminMenuItems, account: accountMenuItems } =
    getNavigation({ selectedBrand, user });

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  // Only the most specific matching item is active, so "Brand Hub"
  // (/brands/4) is not lit up on /brands/4/composer.
  const allPaths = [
    ...mainMenuItems,
    ...brandGroups.flatMap((g) => g.items),
    ...adminMenuItems,
    ...accountMenuItems,
  ].map((i) => i.path);
  const matches = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname === path || location.pathname.startsWith(`${path}/`);
  const activePath = allPaths.filter(matches).sort((a, b) => b.length - a.length)[0];

  const renderItem = (item) => {
    const active = item.path === activePath;
    const button = (
      <ListItemButton
        onClick={() => handleNavigation(item.path)}
        selected={active}
        aria-label={collapsed ? item.text : undefined}
        aria-current={active ? 'page' : undefined}
        sx={{
          mx: collapsed ? 'auto' : 1,
          width: collapsed ? 36 : 'auto',
          flexGrow: collapsed ? 0 : 1,
          height: collapsed ? 34 : { xs: 40, md: 30 },
          px: collapsed ? 0 : 1.25,
          borderRadius: '6px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: active ? '#1B1F3B' : TEXT,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.07)' },
          '&.Mui-selected': {
            backgroundColor: WHITE,
            color: '#1B1F3B',
            '&:hover': { backgroundColor: WHITE },
            '& .MuiListItemIcon-root': { color: 'primary.main' },
          },
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 28,
            justifyContent: 'center',
            color: active ? 'primary.main' : TEXT_MUTED,
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 500, noWrap: true }}
          />
        )}
      </ListItemButton>
    );
    return (
      <ListItem key={item.path} disablePadding sx={{ mb: '2px' }}>
        {collapsed ? (
          <Tooltip title={item.text} placement="right" arrow>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>{button}</Box>
          </Tooltip>
        ) : button}
      </ListItem>
    );
  };

  const renderGroup = (title, items, first = false) => (
    <Box key={title || 'main'} sx={{ mt: first ? 0 : collapsed ? 0 : 1 }}>
      {collapsed ? (
        !first && <Divider sx={{ my: 0.75, mx: 1.5, borderColor: LINE }} />
      ) : (
        title && (
          <Typography
            variant="overline"
            sx={{
              px: 1.5,
              mb: 0.5,
              display: 'block',
              color: TEXT_MUTED,
              fontWeight: 700,
              fontSize: '0.6875rem',
              lineHeight: '16px',
              letterSpacing: '0.08em',
            }}
          >
            {title}
          </Typography>
        )
      )}
      <List disablePadding>{items.map(renderItem)}</List>
    </Box>
  );

  const groups = [
    { title: null, items: mainMenuItems },
    ...brandGroups,
    ...(adminMenuItems.length ? [{ title: 'Administration', items: adminMenuItems }] : []),
    { title: 'Account', items: accountMenuItems },
  ];

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const panelWidth = width - GUTTER * 2;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        transition: 'width 0.2s ease',
        '& .MuiDrawer-paper': {
          width: panelWidth,
          height: `calc(100% - ${GUTTER * 2}px)`,
          m: `${GUTTER}px`,
          boxSizing: 'border-box',
          backgroundColor: 'background.sidebar',
          color: TEXT,
          border: 'none',
          borderRadius: 0,
          overflowX: 'hidden',
          transition: 'width 0.2s ease',
        },
      }}
    >
      <Box
        sx={{
          height: 48,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 0 : 1.5,
        }}
      >
        {collapsed ? <LogoMark size={26} /> : <Logo size={24} textColor={WHITE} />}
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pb: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {groups.map((g, i) => renderGroup(g.title, g.items, i === 0))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          p: 1,
          borderTop: '1px solid',
          borderColor: LINE,
          display: 'flex',
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Tooltip title={collapsed ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : ''} placement="right" arrow>
          <Avatar src={user?.avatar_url} sx={{ width: 30, height: 30, fontSize: '0.6875rem', ml: collapsed ? 0 : 0.5 }}>
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </Avatar>
        </Tooltip>
        {!collapsed && (
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: WHITE, lineHeight: 1.3 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.6875rem', color: TEXT_MUTED, lineHeight: 1.3 }}>
              {user?.role}
            </Typography>
          </Box>
        )}
        {onToggleCollapse && (
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right" arrow>
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              sx={{ color: TEXT_MUTED, borderRadius: '6px', display: { xs: 'none', md: 'flex' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.07)' } }}
            >
              {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
